import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Check, Phone } from 'lucide-react';
import { Reveal, SplitText } from './Reveal';
import MagneticButton from './MagneticButton';
import FrostCard from './FrostCard';
import { BRAND, STEPS_HOME } from '../data/mock';

/**
 * HomeProcess — "Our process is simple & efficient" 3-step section.
 *
 * Extracted from Home.jsx as the largest single-purpose block on the page.
 * Owns its own localStorage state (which steps the visitor has clicked into)
 * so Home.jsx no longer has to plumb that concern through.
 *
 * The middle step (#2) renders on a dark forest FrostCard; steps #1 and #3
 * render on the muted cream FrostCard variant — creating the centerpiece
 * elevation the user asked for early on.
 */
const STORAGE_KEY = 'guardian_steps_visited';
const DESTINATIONS = ['/contact', '/installation', '/projects'];
const CTA_LABELS   = ['Start a quote', 'Our method', 'See projects'];
const META_LABELS  = ['24-hr response', '~1 hour visit', '1—2 days typical'];

export default function HomeProcess() {
  const [stepsVisited, setStepsVisited] = useState({});

  // Hydrate visited-state from localStorage on mount only (intentionally [])
  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
      setStepsVisited(stored);
    } catch (err) {
      console.warn('[HomeProcess] Could not read localStorage:', err);
    }
  }, []);

  const markStepVisited = useCallback((i) => {
    setStepsVisited((prev) => {
      const next = { ...prev, [i]: Date.now() };
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch (err) {
        console.warn('[HomeProcess] Could not persist localStorage:', err);
      }
      return next;
    });
  }, []);

  return (
    <section
      data-testid="home-process"
      className="relative py-16 md:py-40 px-6 md:px-12 border-y border-[#1A1A1A]/8 overflow-hidden"
      style={{
        // Same boots-on-pavers photo as the middle FrostCard, fixed to the
        // viewport so it stays pinned as the user scrolls. Overlay tinted
        // to a soft muted-green wash (built from accent #6A8F3E mixed
        // toward off-white) so the section reads as a quiet green
        // atmosphere rather than cream.  Step 2 (dark FrostCard) shares
        // this exact image so the middle card feels like a "window" onto
        // the same view.
        backgroundImage:
          'linear-gradient(150deg, rgba(233,238,222,0.97), rgba(222,231,206,0.94), rgba(233,238,222,0.97)), url(/textures/process-boots.jpg)',
        backgroundSize: 'cover, cover',
        backgroundAttachment: 'fixed, fixed',
        backgroundPosition: 'center, center 45%',
        backgroundRepeat: 'no-repeat, no-repeat',
      }}
    >
      <div className="absolute -top-40 -right-32 w-[600px] h-[600px] rounded-full bg-[#6A8F3E]/12 blur-[140px]" />
      <div className="absolute -bottom-32 -left-32 w-[500px] h-[500px] rounded-full bg-[#6A8F3E]/10 blur-[140px]" />

      <div className="mx-auto relative" style={{ maxWidth: '1700px' }}>
        <ProcessHeading />

        {/* Connector + 3 step cards */}
        <div className="relative">
          <ConnectorLine />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-8">
            {STEPS_HOME.map((step, i) => (
              <ProcessStepCard
                key={step.title}
                step={step}
                index={i}
                visited={!!stepsVisited[i]}
                onClick={() => markStepVisited(i)}
              />
            ))}
          </div>
        </div>

        <ProcessFooterCTA />
      </div>
    </section>
  );
}

/* -------------------- Internal presentational components -------------------- */

function ProcessHeading() {
  return (
    <div className="text-center mb-16 md:mb-20 max-w-3xl mx-auto">
      <Reveal>
        <div className="eyebrow justify-center mb-7" style={{ display: 'inline-flex' }}>The Guardian Method</div>
      </Reveal>
      <h2 className="font-sans-brand uppercase font-extrabold tracking-tight text-[#1A1A1A] leading-[0.95]" style={{ fontSize: 'clamp(2.4rem, 5vw, 4.4rem)' }}>
        <SplitText text="Our process is" />
        <span className="block font-serif-editorial italic font-light text-[#6A8F3E] mt-3 normal-case" style={{ fontSize: 'clamp(2.6rem, 5.4vw, 5rem)' }}>
          <SplitText text="simple & efficient." delay={0.18} />
        </span>
      </h2>
      <Reveal delay={0.3}>
        <p className="text-base md:text-lg text-[#1A1A1A]/70 leading-relaxed mt-7 max-w-2xl mx-auto">
          From the first call to the final blade of grass — three clearly mapped steps and a single dedicated point of contact throughout.
        </p>
      </Reveal>
    </div>
  );
}

function ConnectorLine() {
  return (
    <div className="hidden md:block absolute top-[88px] left-[16%] right-[16%] h-px">
      <div className="w-full h-full bg-gradient-to-r from-transparent via-[#6A8F3E]/40 to-transparent" />
      <motion.div
        initial={{ x: '0%' }}
        whileInView={{ x: '100%' }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 2.2, ease: [0.16, 1, 0.3, 1] }}
        className="absolute -top-1 -left-1 w-2.5 h-2.5 rounded-full bg-[#6A8F3E] shadow-[0_0_18px_rgba(106,143,62,0.7)]"
      />
    </div>
  );
}

function ProcessStepCard({ step, index, visited, onClick }) {
  const isMid = index === 1;
  const dest = DESTINATIONS[index];
  const ctaLabel = CTA_LABELS[index];
  const metaLabel = META_LABELS[index];

  // Color palette switches between the dark middle card and the muted side cards
  const titleColor      = isMid ? 'text-[#E7E9E4]'        : 'text-[#1A1A1A]';
  const bodyColor       = isMid ? 'text-[#E7E9E4]/75'     : 'text-[#1A1A1A]/70';
  const dividerColor    = isMid ? 'border-[#E7E9E4]/15'   : 'border-[#314F2B]/20';
  const metaLabelColor  = isMid ? 'text-[#E7E9E4]/55'     : 'text-[#1A1A1A]/55';
  const arrowBg         = isMid ? 'bg-[#E7E9E4]/8 border-[#6A8F3E]/50' : 'bg-[#314F2B]/8 border-[#314F2B]/30';
  const ctaHover        = isMid ? 'group-hover:text-[#E7E9E4]' : 'group-hover:text-[#314F2B]';
  const numeralColor    = isMid ? 'text-[#6A8F3E]' : 'text-[#314F2B]';
  const numeralShadow   = isMid ? '0 4px 28px rgba(106,143,62,0.28)' : '0 4px 28px rgba(49,79,43,0.20)';

  return (
    <div>
      <Link
        to={dest}
        onClick={onClick}
        data-cursor="hover"
        data-cursor-label={ctaLabel}
        data-testid={`home-step-${index + 1}-card`}
        className="group relative block rounded-2xl hover:-translate-y-1 hover:shadow-[0_30px_60px_-20px_rgba(22,42,30,0.4)] transition-all duration-500 h-full"
      >
        <FrostCard padding="p-8 md:p-10" className="h-full" variant={isMid ? 'dark' : 'sage'}>
          {visited && (
            <motion.div
              initial={{ opacity: 0, scale: 0.6 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              className="absolute top-4 left-4 z-10 flex items-center gap-1.5 bg-[#6A8F3E] text-[#162A1E] pl-1.5 pr-2.5 py-1 rounded-full shadow-md"
              data-testid={`step-${index + 1}-visited-tick`}
            >
              <span className="w-4 h-4 rounded-full bg-[#162A1E] flex items-center justify-center">
                <Check size={9} className="text-[#6A8F3E]" strokeWidth={3} />
              </span>
              <span className="text-[8px] tracking-[0.32em] uppercase font-extrabold">Explored</span>
            </motion.div>
          )}

          <div className="relative flex items-start justify-between mb-10">
            <motion.div
              initial={{ opacity: 0, scale: 0.6, rotate: -8 }}
              whileInView={{ opacity: 1, scale: 1, rotate: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 1.1, delay: index * 0.15 + 0.25, ease: [0.16, 1, 0.3, 1] }}
              className="relative"
            >
              <span className={`font-serif-editorial text-[110px] md:text-[140px] leading-none transition-colors duration-700 block ${numeralColor}`} style={{ textShadow: numeralShadow }}>
                {step.n}
              </span>
              <motion.div
                initial={{ scale: 0 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6, delay: index * 0.15 + 0.7, ease: [0.16, 1, 0.3, 1] }}
                className="absolute -top-1 -right-3 w-3.5 h-3.5 rounded-full bg-[#6A8F3E] pulse-halo"
              />
            </motion.div>
            <div className="text-right">
              <div className="text-[9px] tracking-[0.3em] uppercase text-[#6A8F3E] font-bold mb-1">Step</div>
              <div className={`text-[9px] tracking-[0.3em] uppercase font-semibold ${metaLabelColor}`}>{String(index + 1).padStart(2, '0')} / 03</div>
            </div>
          </div>

          <div className="relative">
            <h3 className={`font-serif-editorial text-3xl md:text-4xl mb-4 leading-tight ${titleColor}`}>{step.title}</h3>
            <p className={`text-sm md:text-base leading-relaxed mb-8 ${bodyColor}`}>{step.body}</p>

            <div className={`flex items-center justify-between pt-6 border-t ${dividerColor}`}>
              <div>
                <div className={`text-[9px] tracking-[0.2em] uppercase font-bold leading-tight ${metaLabelColor}`}>{metaLabel}</div>
                <div className={`text-[10px] tracking-[0.25em] uppercase text-[#6A8F3E] font-bold mt-1 ${ctaHover} transition-colors`}>{ctaLabel}</div>
              </div>
              <div className={`w-10 h-10 rounded-full border ${arrowBg} group-hover:bg-[#6A8F3E] group-hover:border-[#6A8F3E] flex items-center justify-center transition-all duration-500`}>
                <ArrowRight size={14} className={`${isMid ? 'text-[#6A8F3E]' : 'text-[#314F2B]'} group-hover:text-[#162A1E] group-hover:translate-x-0.5 transition-all`} strokeWidth={2.2} />
              </div>
            </div>
          </div>
        </FrostCard>
      </Link>
    </div>
  );
}

function ProcessFooterCTA() {
  return (
    <Reveal delay={0.4}>
      <div className="mt-14 md:mt-16 flex flex-col md:flex-row items-center justify-center gap-6 md:gap-10">
        <Link to="/contact" data-cursor="hover" data-cursor-label="Start">
          <MagneticButton className="group inline-flex items-center gap-3 bg-[#6A8F3E] hover:bg-[#7BA047] text-[#1A1A1A] pl-8 pr-3 py-3 rounded-full transition-colors duration-500 shadow-[0_10px_30px_-6px_rgba(106,143,62,0.5)]" as="div">
            <span className="text-[11px] tracking-[0.3em] uppercase font-extrabold">Get In Touch</span>
            <span className="w-10 h-10 rounded-full bg-[#1A1A1A] flex items-center justify-center">
              <ArrowRight size={14} className="text-[#6A8F3E]" strokeWidth={2.2} />
            </span>
          </MagneticButton>
        </Link>
        <div className="hidden md:block w-12 h-px bg-[#1A1A1A]/20" />
        <a href={`tel:${BRAND.phoneTel}`} data-cursor="hover" data-cursor-label="Call" className="inline-flex items-center gap-3 group">
          <div className="w-11 h-11 rounded-full border border-[#314F2B]/30 group-hover:bg-[#6A8F3E]/10 flex items-center justify-center transition-all">
            <Phone size={14} className="text-[#314F2B]" strokeWidth={1.6} />
          </div>
          <div>
            <div className="text-[9px] tracking-[0.3em] uppercase text-[#1A1A1A]/55 font-semibold">Or call us on</div>
            <div className="text-base font-bold text-[#1A1A1A] group-hover:text-[#6A8F3E] transition-colors">{BRAND.phone}</div>
          </div>
        </a>
      </div>
    </Reveal>
  );
}
